package org.teddapp.project.ui.skills

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Assistant_conversation
import com.tedd.db.Assistant_message
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.teddapp.project.db.repo.AssistantRepository
import org.teddapp.project.server.ServerException
import org.teddapp.project.server.ServerSession
import org.teddapp.project.sync.EntityKind
import org.teddapp.project.sync.ServerIds
import org.teddapp.project.sync.pushNow

/** Ce dont l'écran a besoin du serveur ; remplacé par un faux dans les tests. */
internal interface AssistantServer {
    /** Envoie la compétence et la conversation si le serveur ne les a pas encore. */
    suspend fun prepare(skillId: String, conversationId: String)

    /** Pose la question ; [onEvent] reçoit chaque événement du flux. */
    suspend fun ask(skillId: String, conversationId: String, question: String, onEvent: suspend (String, String) -> Unit)
}

internal class SessionAssistantServer(private val db: TeddDatabase, private val session: ServerSession) : AssistantServer {
    private fun serverId(local: String) = if (ServerIds.isUuid(local)) local.lowercase() else ServerIds.derive(local)

    override suspend fun prepare(skillId: String, conversationId: String) {
        pushNow(db, session, listOf(EntityKind.Skill to skillId, EntityKind.AssistantConversation to conversationId))
    }

    override suspend fun ask(skillId: String, conversationId: String, question: String, onEvent: suspend (String, String) -> Unit) {
        session.authed { api, access -> api.ask(access, serverId(skillId), serverId(conversationId), question, onEvent) }
    }
}

/** Une source citée par une réponse. */
internal data class Source(
    val resourceId: String,
    val title: String,
    val page: Int?,
    val slide: Int?,
    val excerpt: String,
) {
    /** « p. 12 », « diapositive 3 », ou rien. */
    val where: String? get() = page?.let { "p. $it" } ?: slide?.let { "diapositive $it" }

    companion object {
        fun parseAll(json: String?): List<Source> {
            if (json.isNullOrBlank()) return emptyList()
            val array = runCatching { Json.parseToJsonElement(json).jsonArray }.getOrNull() ?: return emptyList()
            return parseAll(array)
        }

        fun parseAll(array: JsonArray): List<Source> = array.mapNotNull { el ->
            val o = el as? JsonObject ?: return@mapNotNull null
            Source(
                resourceId = o["resource_id"]?.jsonPrimitive?.contentOrNull ?: return@mapNotNull null,
                title = o["title"]?.jsonPrimitive?.contentOrNull.orEmpty(),
                page = o["page"]?.jsonPrimitive?.intOrNull,
                slide = o["slide"]?.jsonPrimitive?.intOrNull,
                excerpt = o["excerpt"]?.jsonPrimitive?.contentOrNull.orEmpty(),
            )
        }
    }
}

/** La réponse en cours d'arrivée, affichée avant que le message enregistré arrive par le pull. */
internal data class LiveAnswer(
    val conversationId: String,
    val question: String,
    val text: String = "",
    val sources: List<Source> = emptyList(),
    val questionId: String? = null,
    val answerId: String? = null,
    /** null tant que la réponse arrive ; puis complete, interrupted ou failed. */
    val status: String? = null,
    val error: String? = null,
)

/** Un message tel que la conversation le montre. */
internal data class ShownMessage(
    val id: String,
    val fromUser: Boolean,
    val text: String,
    val sources: List<Source>,
    /** streaming, complete, interrupted, failed. */
    val status: String,
    val note: String?,
)

internal object AssistantTexts {
    const val UNTITLED = "Nouvelle conversation"
    const val QUESTION_MAX = 2_000
    /** Au-delà, une réponse encore « en cours » est tenue pour abandonnée (5 minutes). */
    const val STALE_MS = 5L * 60 * 1000

    /** Les refus qui se règlent dans les réglages : le bouton « Réglages » les accompagne. */
    private val SETTINGS_CODES = listOf("no_model", "model_unauthorized", "model_not_found", "model_blocked", "model_quota", "model_unavailable")

    /** Vrai si ce message est celui d'un refus qui se règle dans les réglages. */
    fun sendsToSettings(message: String): Boolean = SETTINGS_CODES.any { errorMessage(it) == message }

    /** Le message d'un refus du serveur, avant ou pendant la réponse. */
    fun errorMessage(code: String?): String = when (code) {
        "no_ready_resource" -> "Aucune ressource n'est prête dans cette compétence. Ajoute un cours, un lien ou tes notes, puis attends qu'ils soient « Prête »."
        "no_model" -> "Choisis d'abord le modèle de l'assistant dans les réglages : un service en ligne avec ta clé, ou ton propre serveur."
        "model_unauthorized" -> "Le service du modèle refuse ta clé. Vérifie-la dans les réglages."
        "model_quota" -> "Le quota de ton service de modèle est atteint. Réessaie plus tard, ou change de modèle dans les réglages."
        "model_not_found" -> "Le service ne connaît pas ce modèle. Vérifie son nom dans les réglages."
        "model_blocked" -> "L'adresse du modèle n'est pas joignable depuis le serveur. Choisis un service en ligne, ou une adresse publique."
        "model_unavailable" -> "Le modèle ne répond pas. Réessaie dans un instant, ou vérifie son adresse dans les réglages."
        "model_failed" -> "Le modèle a répondu une erreur."
        "busy" -> "Une réponse est déjà en cours dans cette conversation. Attends sa fin, ou ouvre une autre conversation."
        "rag_unavailable" -> "L'assistant est indisponible pour le moment. Réessaie dans un instant."
        "too_many_requests" -> "Trop de questions d'un coup : réessaie dans une minute."
        "quota_exceeded" -> "Cette conversation a atteint 500 messages. Commence-en une autre."
        "invalid" -> "La question doit faire de 1 à 2 000 caractères."
        "not_found" -> "Le serveur ne connaît pas cette conversation. Vérifie que l'appareil est connecté, puis réessaie."
        "timeout" -> "L'assistant a mis trop de temps à répondre."
        "rag_failed" -> "L'assistant n'a pas pu répondre."
        "interrupted", "stream_cut" -> "La réponse s'est interrompue."
        "unreachable" -> "Le serveur est injoignable. Vérifie ta connexion."
        "offline" -> "Connecte-toi au serveur pour poser une question à l'assistant."
        "conversation_refused" -> "La compétence a déjà 200 conversations. Supprimes-en une pour en ouvrir une autre."
        else -> "L'assistant n'a pas pu répondre."
    }

    fun noteFor(status: String, errorCode: String?): String? = when (status) {
        "interrupted" -> "Réponse interrompue"
        "failed" -> errorMessage(errorCode)
        "streaming" -> "Réponse en cours…"
        else -> null
    }

    /** Le début d'une réponse, pour la liste : une ligne, coupée. */
    fun preview(text: String, max: Int = 110): String {
        val flat = text.replace(Regex("[#*_`>]"), "").split(Regex("\\s+")).filter { it.isNotEmpty() }.joinToString(" ")
        return if (flat.length <= max) flat else flat.take(max).trimEnd() + "…"
    }
}

/**
 * L'assistant d'une compétence : ses conversations, celle qui est ouverte, la question en
 * cours. L'historique vient de la base locale ; la réponse arrive en flux du serveur, qui
 * enregistre lui-même la question et la réponse. L'appareil ne les écrit pas : la réponse
 * en cours est montrée, puis remplacée par le message enregistré quand il arrive.
 */
internal class AssistantModel(
    private val repo: AssistantRepository,
    val skillId: String,
    private val scope: CoroutineScope,
    private val server: () -> AssistantServer?,
    /** Demande une synchronisation : les messages enregistrés arrivent par le pull. */
    private val requestSync: () -> Unit = {},
    private val now: () -> Long = { org.teddapp.project.db.nowMillis() },
) {
    var conversations by mutableStateOf<List<Assistant_conversation>>(emptyList())
    var trash by mutableStateOf<List<Assistant_conversation>>(emptyList())
    /** Le début de la dernière réponse, par conversation. */
    var lastAnswers by mutableStateOf<Map<String, String>>(emptyMap())
    var messages by mutableStateOf<List<Assistant_message>>(emptyList())

    var search by mutableStateOf("")
    var selectedId by mutableStateOf<String?>(null)
    var draft by mutableStateOf("")
    var live by mutableStateOf<LiveAnswer?>(null)
    var notice by mutableStateOf<String?>(null)
    /** La dernière conversation supprimée, pour « Annuler ». */
    var undoable by mutableStateOf<Assistant_conversation?>(null)

    var renaming by mutableStateOf<Assistant_conversation?>(null)
    var renameText by mutableStateOf("")
    var renameError by mutableStateOf<String?>(null)

    private var job: Job? = null

    val visible: List<Assistant_conversation>
        get() {
            val q = search.trim().lowercase()
            if (q.isEmpty()) return conversations
            return conversations.filter { (it.title ?: AssistantTexts.UNTITLED).lowercase().contains(q) }
        }

    val selected: Assistant_conversation? get() = conversations.firstOrNull { it.id == selectedId }

    val asking: Boolean get() = live?.let { it.status == null } == true

    fun titleOf(c: Assistant_conversation): String = c.title?.takeIf { it.isNotBlank() } ?: AssistantTexts.UNTITLED

    /** Les messages de la conversation ouverte, avec la réponse en cours par-dessus. */
    val shown: List<ShownMessage>
        get() {
            val stored = messages.map { m ->
                val stale = m.status == "streaming" && now() - m.created_at > AssistantTexts.STALE_MS
                val status = if (stale) "interrupted" else m.status
                ShownMessage(m.id, m.role == "user", m.content, Source.parseAll(m.sources), status, AssistantTexts.noteFor(status, m.error_code))
            }
            val l = live?.takeIf { it.conversationId == selectedId } ?: return stored
            val ids = messages.associateBy { it.id }
            val out = stored.filter { s ->
                // La réponse enregistrée remplace la réponse en cours dès qu'elle est finie.
                val m = ids[s.id]
                !(s.id == l.answerId && m?.status == "streaming")
            }.toMutableList()
            if (l.questionId == null || l.questionId !in ids) {
                out += ShownMessage(l.questionId ?: "live-question", true, l.question, emptyList(), "complete", null)
            }
            val answerStored = l.answerId != null && ids[l.answerId]?.status?.let { it != "streaming" } == true
            if (!answerStored) {
                val status = l.status ?: "streaming"
                out += ShownMessage(
                    l.answerId ?: "live-answer", false, l.text, l.sources, status,
                    when (status) {
                        "streaming" -> null
                        "interrupted" -> "Réponse interrompue"
                        "failed" -> l.error
                        else -> null
                    },
                )
            }
            return out
        }

    fun open(id: String) {
        selectedId = id
        notice = null
    }

    fun close() {
        selectedId = null
    }

    /** Ouvre une nouvelle conversation, vide ; elle part au serveur avec sa première question. */
    fun newConversation() {
        scope.launch {
            val id = repo.create(skillId)
            if (id == null) notice = AssistantTexts.errorMessage("conversation_refused") else open(id)
        }
    }

    fun send() {
        val conversationId = selectedId ?: return
        val question = draft.trim()
        if (question.isEmpty() || asking) return
        if (question.length > AssistantTexts.QUESTION_MAX) {
            notice = AssistantTexts.errorMessage("invalid")
            return
        }
        val s = server()
        if (s == null) {
            notice = AssistantTexts.errorMessage("offline")
            return
        }
        draft = ""
        notice = null
        live = LiveAnswer(conversationId, question)
        job = scope.launch {
            var started = false
            try {
                s.prepare(skillId, conversationId)
                if (repo.get(conversationId)?.push_error != null) throw ServerException(409, "conversation_refused", "")
                s.ask(skillId, conversationId, question) { event, data -> started = true; onEvent(event, data) }
                // Le flux s'est fermé sans fin annoncée : il a été coupé.
                if (live?.status == null) update { it.copy(status = "interrupted") }
            } catch (e: kotlinx.coroutines.CancellationException) {
                // « Arrêter » : le serveur garde ce qui a été reçu, marqué interrompu.
                if (live?.status == null) update { it.copy(status = "interrupted") }
                throw e
            } catch (e: ServerException) {
                if (!started) {
                    // Refusée avant le flux : rien n'est enregistré, la question revient dans le champ.
                    live = null
                    draft = question
                    notice = AssistantTexts.errorMessage(e.code)
                } else {
                    update { it.copy(status = "interrupted", error = AssistantTexts.errorMessage(e.code)) }
                }
            } catch (e: Throwable) {
                if (!started) {
                    live = null
                    draft = question
                    notice = AssistantTexts.errorMessage("unreachable")
                } else {
                    update { it.copy(status = "interrupted") }
                }
            } finally {
                requestSync()
            }
        }
    }

    /** « Arrêter » : coupe le flux ; la réponse reste visible, « Réponse interrompue ». */
    fun stop() {
        job?.cancel()
        update { if (it.status == null) it.copy(status = "interrupted") else it }
    }

    private fun update(change: (LiveAnswer) -> LiveAnswer) {
        live = live?.let(change)
    }

    private fun onEvent(event: String, data: String) {
        val json = runCatching { Json.parseToJsonElement(data) }.getOrNull()
        when (event) {
            "message" -> {
                val o = json?.jsonObject ?: return
                update {
                    it.copy(
                        questionId = o["user_message_id"]?.jsonPrimitive?.contentOrNull,
                        answerId = o["assistant_message_id"]?.jsonPrimitive?.contentOrNull,
                    )
                }
            }
            "sources" -> (json as? JsonArray)?.let { array -> update { it.copy(sources = Source.parseAll(array)) } }
            "delta" -> json?.jsonObject?.get("text")?.jsonPrimitive?.contentOrNull?.let { piece -> update { it.copy(text = it.text + piece) } }
            "done" -> update { it.copy(status = "complete") }
            "error" -> {
                val code = json?.jsonObject?.get("code")?.jsonPrimitive?.contentOrNull
                update {
                    if (code == "interrupted") it.copy(status = "interrupted")
                    else it.copy(status = "failed", error = AssistantTexts.errorMessage(code))
                }
            }
        }
    }

    /** La réponse en cours n'a plus lieu d'être quand le message enregistré est arrivé. */
    fun settle() {
        val l = live ?: return
        if (l.status == null) return
        val stored = messages.firstOrNull { it.id == l.answerId }
        if (stored != null && stored.status != "streaming") live = null
    }

    fun startRename(c: Assistant_conversation) {
        renaming = c
        renameText = c.title.orEmpty()
        renameError = null
    }

    fun confirmRename() {
        val c = renaming ?: return
        scope.launch {
            val problem = repo.rename(c.id, renameText)
            if (problem == null) renaming = null else renameError = problem
        }
    }

    fun delete(c: Assistant_conversation) {
        scope.launch {
            repo.delete(c.id)
            if (selectedId == c.id) selectedId = null
            undoable = c
            notice = "« ${titleOf(c)} » est supprimée."
        }
    }

    /** Annule la dernière suppression. */
    fun undo() {
        val c = undoable ?: return
        undoable = null
        restore(c)
    }

    fun restore(c: Assistant_conversation) {
        scope.launch {
            val problem = repo.restore(c.id)
            notice = problem ?: "« ${titleOf(c)} » est restaurée."
        }
    }

    /** Le lien d'une source à ouvrir, ou ce qu'on en montre ; null si la ressource n'existe plus. */
    suspend fun resourceOf(source: Source): com.tedd.db.Skill_resource? = repo.resource(source.resourceId)
}

/** Le modèle de l'assistant de la compétence [skillId], branché sur la base locale. */
@Composable
internal fun rememberAssistantModel(
    repo: AssistantRepository,
    db: TeddDatabase,
    skillId: String,
    session: ServerSession?,
    requestSync: () -> Unit,
): AssistantModel {
    val scope = rememberCoroutineScope()
    val model = remember(repo, skillId) {
        AssistantModel(repo, skillId, scope, { session?.takeIf { it.connected }?.let { SessionAssistantServer(db, it) } }, requestSync)
    }
    model.conversations = remember(repo, skillId) { repo.observeConversations(skillId) }.collectAsState(emptyList()).value
    model.trash = remember(repo, skillId) { repo.observeTrash(skillId) }.collectAsState(emptyList()).value
    val answers = remember(repo, skillId) { repo.observeLastAnswers(skillId) }.collectAsState(emptyList()).value
    model.lastAnswers = remember(answers) { answers.associate { it.conversation_id to AssistantTexts.preview(it.content) } }
    val selected = model.selectedId
    model.messages = remember(repo, selected) { selected?.let(repo::observeMessages) ?: flowOf(emptyList()) }.collectAsState(emptyList()).value
    LaunchedEffect(model.messages, model.live?.status) { model.settle() }
    LaunchedEffect(model.notice) {
        if (model.notice != null) {
            kotlinx.coroutines.delay(8_000)
            model.notice = null
            model.undoable = null
        }
    }
    return model
}
